import { useState, useEffect, useRef, useCallback } from "react";
import { GripVerticalIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { IframePanel } from "./IframePanel";
import { ChatContainer } from "@/components/chat/ChatContainer";

type LayoutMode = "split" | "chat" | "browser";

const MOBILE_BREAKPOINT = 768;
const SPLIT_MIN = 15;
const SPLIT_MAX = 85;
const SPLIT_DEFAULT_MOBILE = 60;  // browser panel % (top)
const SPLIT_DEFAULT_DESKTOP = 30; // chat panel % (left)

/** Responsive media-query hook */
function useIsMobile() {
  const [mobile, setMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth < MOBILE_BREAKPOINT
  );

  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);
    const onChange = (e: MediaQueryListEvent | MediaQueryList) => setMobile(e.matches);
    onChange(mq);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  return mobile;
}

/** Clamp a number between min and max */
const clamp = (v: number, min: number, max: number) =>
  Math.max(min, Math.min(max, v));

export function ContentArea() {
  const [layout, setLayout] = useState<LayoutMode>("split");
  const isMobile = useIsMobile();
  const [splitPct, setSplitPct] = useState(isMobile ? SPLIT_DEFAULT_MOBILE : SPLIT_DEFAULT_DESKTOP);
  const containerRef = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);

  // Reset split ratio when viewport class changes
  useEffect(() => {
    setSplitPct(isMobile ? SPLIT_DEFAULT_MOBILE : SPLIT_DEFAULT_DESKTOP);
  }, [isMobile]);

  // --- Drag-to-resize (pointer events) ---
  const onPointerDown = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    dragging.current = true;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }, []);

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!dragging.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const pct = isMobile
        ? ((e.clientY - rect.top) / rect.height) * 100
        : ((e.clientX - rect.left) / rect.width) * 100;
      setSplitPct(clamp(pct, SPLIT_MIN, SPLIT_MAX));
    },
    [isMobile]
  );

  const onPointerUp = useCallback(() => {
    dragging.current = false;
  }, []);

  const isSplit = layout === "split";

  /*
   * DOM order is always: Chat → Handle → Browser.
   * CSS `order` swaps the visual position:
   *   Desktop: Chat (order-1, left) | Handle | Browser (order-3, right)
   *   Mobile:  Browser (order-1, top) | Handle | Chat (order-3, bottom)
   * This keeps React identity stable so iframes never remount.
   */
  return (
    <div className="h-full w-full p-4" ref={containerRef}>
      <div className={cn("flex h-full w-full", isMobile ? "flex-col" : "flex-row")}>

        {/* ── Chat Panel (always mounted) ── */}
        <div
          className={cn(
            "min-h-0 min-w-0 overflow-hidden",
            !isSplit && layout === "chat" && "flex-1",
            !isSplit && layout === "browser" && "hidden",
            isMobile ? "order-3" : "order-1",
          )}
          style={
            isSplit
              ? isMobile
                ? { height: `${100 - splitPct}%` }
                : { width: `${splitPct}%` }
              : undefined
          }
        >
          <ChatContainer
            isExpanded={!isSplit}
            onToggleExpand={isSplit ? () => setLayout("chat") : () => setLayout("split")}
          />
        </div>

        {/* ── Resize Handle ── */}
        {isSplit && (
          <div
            className={cn(
              "order-2 flex shrink-0 items-center justify-center select-none touch-none",
              isMobile ? "h-3 w-full cursor-row-resize" : "w-3 h-full cursor-col-resize",
            )}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
          >
            <div
              className={cn(
                "bg-border flex items-center justify-center rounded-sm border",
                isMobile ? "h-3 w-12" : "h-8 w-3",
              )}
            >
              <GripVerticalIcon className={cn("size-2.5 text-muted-foreground", isMobile && "rotate-90")} />
            </div>
          </div>
        )}

        {/* ── Browser Panel (always mounted — preserves iframe refs) ── */}
        <div
          className={cn(
            "min-h-0 min-w-0 overflow-hidden",
            !isSplit && layout === "browser" && "flex-1",
            !isSplit && layout === "chat" && "hidden",
            isMobile ? "order-1" : "order-3",
          )}
          style={
            isSplit
              ? isMobile
                ? { height: `${splitPct}%` }
                : { width: `${100 - splitPct}%` }
              : undefined
          }
        >
          <IframePanel
            isExpanded={!isSplit}
            onToggleExpand={isSplit ? () => setLayout("browser") : () => setLayout("split")}
          />
        </div>

      </div>
    </div>
  );
}
